/**
 * 统一 API 客户端
 * - 请求拦截：自动附加 Bearer Token
 * - 响应拦截：解包 { code, data, message } 信封；非 0 抛出 ApiError
 * - 401 自动刷新双 token（互斥锁 + 请求队列），刷新失败则清空并跳转登录
 */
import axios, { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { ErrorCodes } from '@yingai/shared';
import { tokenStorage } from './storage';

export interface ApiEnvelope<T = unknown> {
  code: number;
  data: T;
  message: string;
}

export class ApiError extends Error {
  code: number;
  constructor(code: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
  }
}

const BASE_URL = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api';

const client: AxiosInstance = axios.create({ baseURL: BASE_URL, timeout: 20000 });

client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = tokenStorage.access;
  if (token) config.headers.set('Authorization', `Bearer ${token}`);
  return config;
});

let refreshing = false;
const queue: Array<(token: string | null) => void> = [];

function flush(token: string | null): void {
  queue.forEach((cb) => cb(token));
  queue.length = 0;
}

/** 刷新请求超时:防止 /auth/refresh 挂起导致 refreshing 永久为 true、全站请求排队假死 */
const REFRESH_TIMEOUT_MS = 10000;

async function doRefresh(): Promise<string | null> {
  const refresh = tokenStorage.refresh;
  if (!refresh) return null;
  try {
    const resp = await axios.post<ApiEnvelope<{ accessToken: string; refreshToken: string; expiresIn: number }>>(
      `${BASE_URL}/auth/refresh`,
      { refreshToken: refresh },
      { timeout: REFRESH_TIMEOUT_MS },
    );
    const tokens = resp.data.data;
    if (!tokens?.accessToken) return null;
    tokenStorage.set(tokens.accessToken, tokens.refreshToken);
    return tokens.accessToken;
  } catch {
    return null;
  }
}

function redirectLogin(): void {
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
    window.location.href = '/login';
  }
}

/** 重放原请求时带上重试标记,防止再次 401 后重入 handleRefresh 形成死锁 */
const RETRIED_FLAG = '__authRetried';

async function handleRefresh(original: InternalAxiosRequestConfig): Promise<unknown> {
  // 已重试过一次仍 401:不再进入刷新流程,直接报登录失效,避免重入死锁
  if ((original as InternalAxiosRequestConfig & { [RETRIED_FLAG]?: boolean })[RETRIED_FLAG]) {
    tokenStorage.clear();
    redirectLogin();
    return Promise.reject(new ApiError(ErrorCodes.UNAUTHORIZED, '登录已失效，请重新登录'));
  }
  if (refreshing) {
    return new Promise((resolve, reject) => {
      queue.push((token) => {
        if (token) {
          original.headers.set('Authorization', `Bearer ${token}`);
          resolve(client(original));
        } else {
          reject(new ApiError(ErrorCodes.UNAUTHORIZED, '登录已失效，请重新登录'));
        }
      });
    });
  }
  refreshing = true;
  try {
    const token = await doRefresh();
    flush(token);
    if (!token) {
      tokenStorage.clear();
      redirectLogin();
      return null;
    }
    original.headers.set('Authorization', `Bearer ${token}`);
    (original as InternalAxiosRequestConfig & { [RETRIED_FLAG]?: boolean })[RETRIED_FLAG] = true;
    return await client(original);
  } catch (e) {
    flush(null);
    tokenStorage.clear();
    redirectLogin();
    return null;
  } finally {
    refreshing = false;
  }
}

client.interceptors.response.use(
  (response: AxiosResponse<ApiEnvelope>) => {
    const env = response.data;
    if (env && typeof env === 'object' && 'code' in env && 'data' in env) {
      if (env.code !== 0) throw new ApiError(env.code, env.message || '请求失败');
      return env.data as unknown as AxiosResponse;
    }
    return response.data as unknown as AxiosResponse;
  },
  async (error: AxiosError<ApiEnvelope>) => {
    const status = error.response?.status;
    const env = error.response?.data;
    if (env && typeof env === 'object' && 'code' in env) {
      if (status === 401 && env.code === ErrorCodes.UNAUTHORIZED) {
        const original = error.config as InternalAxiosRequestConfig | undefined;
        if (!original) return Promise.reject(new ApiError(env.code, env.message || '请重新登录'));
        const retried = await handleRefresh(original);
        if (retried !== null && retried !== undefined) return retried;
        tokenStorage.clear();
        redirectLogin();
        return Promise.reject(new ApiError(env.code, env.message || '登录已失效'));
      }
      return Promise.reject(new ApiError(env.code, env.message || '请求失败'));
    }
    return Promise.reject(new ApiError(50000, error.message || '网络错误'));
  },
);

export { client, BASE_URL };
export default client;
